import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Mail, MapPin, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

// Departments list
const DEPARTEMENTS = [
  "01 - Ain", "02 - Aisne", "03 - Allier", "04 - Alpes-de-Haute-Provence", "05 - Hautes-Alpes",
  "06 - Alpes-Maritimes", "07 - Ardèche", "08 - Ardennes", "09 - Ariège", "10 - Aube",
  "11 - Aude", "12 - Aveyron", "13 - Bouches-du-Rhône", "14 - Calvados", "15 - Cantal",
  "16 - Charente", "17 - Charente-Maritime", "18 - Cher", "19 - Corrèze", "2A - Corse-du-Sud",
  "2B - Haute-Corse", "21 - Côte-d'Or", "22 - Côtes-d'Armor", "23 - Creuse", "24 - Dordogne",
  "25 - Doubs", "26 - Drôme", "27 - Eure", "28 - Eure-et-Loir", "29 - Finistère",
  "30 - Gard", "31 - Haute-Garonne", "32 - Gers", "33 - Gironde", "34 - Hérault",
  "35 - Ille-et-Vilaine", "36 - Indre", "37 - Indre-et-Loire", "38 - Isère", "39 - Jura",
  "40 - Landes", "41 - Loir-et-Cher", "42 - Loire", "43 - Haute-Loire", "44 - Loire-Atlantique",
  "45 - Loiret", "46 - Lot", "47 - Lot-et-Garonne", "48 - Lozère", "49 - Maine-et-Loire",
  "50 - Manche", "51 - Marne", "52 - Haute-Marne", "53 - Mayenne", "54 - Meurthe-et-Moselle",
  "55 - Meuse", "56 - Morbihan", "57 - Moselle", "58 - Nièvre", "59 - Nord",
  "60 - Oise", "61 - Orne", "62 - Pas-de-Calais", "63 - Puy-de-Dôme", "64 - Pyrénées-Atlantiques",
  "65 - Hautes-Pyrénées", "66 - Pyrénées-Orientales", "67 - Bas-Rhin", "68 - Haut-Rhin", "69 - Rhône",
  "70 - Haute-Saône", "71 - Saône-et-Loire", "72 - Sarthe", "73 - Savoie", "74 - Haute-Savoie",
  "75 - Paris", "76 - Seine-Maritime", "77 - Seine-et-Marne", "78 - Yvelines", "79 - Deux-Sèvres",
  "80 - Somme", "81 - Tarn", "82 - Tarn-et-Garonne", "83 - Var", "84 - Vaucluse",
  "85 - Vendée", "86 - Vienne", "87 - Haute-Vienne", "88 - Vosges", "89 - Yonne",
  "90 - Territoire de Belfort", "91 - Essonne", "92 - Hauts-de-Seine", "93 - Seine-Saint-Denis",
  "94 - Val-de-Marne", "95 - Val-d'Oise", "971 - Guadeloupe", "972 - Martinique",
  "973 - Guyane", "974 - La Réunion", "976 - Mayotte"
];

export default function ContactPage() {
  const [formData, setFormData] = useState({ nom: "", prenom: "", email: "", telephone: "", departement: "", objet: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.nom || formData.nom.length < 2) newErrors.nom = "Nom requis";
    if (!formData.prenom || formData.prenom.length < 2) newErrors.prenom = "Prénom requis";
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Email invalide";
    if (!formData.telephone || formData.telephone.length < 10) newErrors.telephone = "Téléphone requis";
    if (!formData.departement) newErrors.departement = "Département requis";
    if (!formData.objet || formData.objet.length < 10) newErrors.objet = "Objet requis (min. 10 car.)";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { toast.error("Veuillez corriger les erreurs"); return; }
    setIsSubmitting(true); setSubmitStatus(null);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus("success"); toast.success("Demande envoyée !");
      setFormData({ nom: "", prenom: "", email: "", telephone: "", departement: "", objet: "" });
    } catch (error) {
      if (error.response?.status === 429) toast.error("Trop de demandes. Réessayez plus tard.");
      else toast.error("Erreur lors de l'envoi.");
      setSubmitStatus("error");
    } finally { setIsSubmitting(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  return (
    <>
      <Helmet>
        <title>Contact | FLOW - Demande de devis climatisation Paris</title>
        <meta name="description" content="Contactez FLOW pour votre projet de climatisation ou ventilation à Paris et en Île-de-France. Devis gratuit sous 24h. Tél : 06 50 44 88 40" />
        <link rel="canonical" href="https://flow-air.com/contact" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#F5F7FA] to-white py-16 md:py-24">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto">
            <span className="caption mb-4 block">Contact</span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0E1A2B] mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Parlons de votre <span className="text-[#00AFFF]">projet</span>
            </h1>
            <p className="text-lg text-[#475569] leading-relaxed">
              Notre équipe est à votre écoute pour étudier vos besoins en climatisation et ventilation. 
              Réponse garantie sous 24 heures.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24">
        <div className="container-flow">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            
            {/* Contact Info */}
            <motion.div {...fadeInUp}>
              <h2 className="text-2xl font-bold text-[#0E1A2B] mb-8">Nos coordonnées</h2>
              
              <div className="space-y-6 mb-10">
                <a href="tel:+33650448840" className="flex items-center gap-4 p-4 bg-[#F5F7FA] rounded-xl hover:bg-[#E8F4FD] transition-colors group">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#00AFFF] to-[#0099DD] flex items-center justify-center">
                    <Phone size={20} className="text-white" />
                  </div>
                  <div>
                    <span className="caption block mb-0.5">Téléphone</span>
                    <span className="text-lg font-semibold text-[#0E1A2B] group-hover:text-[#00AFFF] transition-colors">06 50 44 88 40</span>
                  </div>
                </a>

                <a href="mailto:contact@flow-air.com" className="flex items-center gap-4 p-4 bg-[#F5F7FA] rounded-xl hover:bg-[#E8F4FD] transition-colors group">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#00AFFF] to-[#0099DD] flex items-center justify-center">
                    <Mail size={20} className="text-white" />
                  </div>
                  <div>
                    <span className="caption block mb-0.5">Email</span>
                    <span className="text-lg font-semibold text-[#0E1A2B] group-hover:text-[#00AFFF] transition-colors">contact@flow-air.com</span>
                  </div>
                </a>

                <div className="flex items-center gap-4 p-4 bg-[#F5F7FA] rounded-xl">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#00AFFF] to-[#0099DD] flex items-center justify-center">
                    <MapPin size={20} className="text-white" />
                  </div>
                  <div>
                    <span className="caption block mb-0.5">Adresse</span>
                    <span className="text-lg font-semibold text-[#0E1A2B]">9 rue Quentin, 93300 Aubervilliers</span>
                  </div>
                </div>
              </div>

              {/* Zone d'intervention */}
              <div className="bg-[#0E1A2B] rounded-2xl p-6 md:p-8">
                <h3 className="text-lg font-semibold text-white mb-4">Zone d'intervention</h3>
                <p className="text-[#94A3B8] mb-5">
                  Nous intervenons sur <span className="text-white font-medium">toute la France</span> pour vos projets de climatisation et ventilation.
                </p>
                <div className="border-t border-white/10 pt-4">
                  <p className="text-[#64748B] text-xs mb-2">Réalisations récentes :</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-white/10 text-white text-sm rounded-full">Paris — Restaurant Anima</span>
                    <span className="px-3 py-1 bg-white/10 text-white text-sm rounded-full">Boulogne-Billancourt — Studio Calogero</span>
                    <span className="px-3 py-1 bg-white/10 text-white text-sm rounded-full">Toulouse — Studio After Now</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }} 
              transition={{ duration: 0.6 }}
            >
              <div className="bg-white rounded-2xl shadow-xl border border-[#E2E8F0] p-6 md:p-8">
                <h2 className="text-2xl font-bold text-[#0E1A2B] mb-2">Demande de devis</h2>
                <p className="text-[#475569] mb-6 text-sm">Remplissez le formulaire, nous vous recontactons sous 24h.</p>

                <AnimatePresence mode="wait">
                  {submitStatus === "success" ? (
                    <motion.div className="text-center py-10" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#00AFFF] to-[#0099DD] flex items-center justify-center mb-6 shadow-lg mx-auto">
                        <Check size={32} className="text-white" />
                      </div>
                      <h3 className="text-xl font-semibold mb-3 text-[#0E1A2B]">Demande envoyée</h3>
                      <p className="text-[#475569] max-w-sm mx-auto mb-6 text-sm">Un spécialiste FLOW vous contactera sous 24 heures pour votre devis personnalisé.</p>
                      <button onClick={() => setSubmitStatus(null)} className="btn-secondary text-sm">Nouvelle demande</button>
                    </motion.div>
                  ) : (
                    <motion.form onSubmit={handleSubmit} initial={{ opacity: 1 }} exit={{ opacity: 0 }}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label htmlFor="nom" className="caption block mb-1.5">Nom *</label>
                          <input type="text" id="nom" name="nom" value={formData.nom} onChange={handleChange} className={`input-field ${errors.nom ? 'border-red-400' : ''}`} placeholder="Votre nom" />
                          {errors.nom && <span className="text-red-500 text-xs mt-1 block">{errors.nom}</span>}
                        </div>
                        <div>
                          <label htmlFor="prenom" className="caption block mb-1.5">Prénom *</label>
                          <input type="text" id="prenom" name="prenom" value={formData.prenom} onChange={handleChange} className={`input-field ${errors.prenom ? 'border-red-400' : ''}`} placeholder="Votre prénom" />
                          {errors.prenom && <span className="text-red-500 text-xs mt-1 block">{errors.prenom}</span>}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label htmlFor="email" className="caption block mb-1.5">Email *</label>
                          <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={`input-field ${errors.email ? 'border-red-400' : ''}`} placeholder="votre@email.com" />
                          {errors.email && <span className="text-red-500 text-xs mt-1 block">{errors.email}</span>}
                        </div>
                        <div>
                          <label htmlFor="telephone" className="caption block mb-1.5">Téléphone *</label>
                          <input type="tel" id="telephone" name="telephone" value={formData.telephone} onChange={handleChange} className={`input-field ${errors.telephone ? 'border-red-400' : ''}`} placeholder="06 00 00 00 00" />
                          {errors.telephone && <span className="text-red-500 text-xs mt-1 block">{errors.telephone}</span>}
                        </div>
                      </div>
                      <div className="mb-4">
                        <label htmlFor="departement" className="caption block mb-1.5">Département *</label>
                        <select id="departement" name="departement" value={formData.departement} onChange={handleChange} className={`input-field ${errors.departement ? 'border-red-400' : ''}`}>
                          <option value="">Sélectionnez un département</option>
                          {DEPARTEMENTS.map((dep) => (
                            <option key={dep} value={dep}>{dep}</option>
                          ))}
                        </select>
                        {errors.departement && <span className="text-red-500 text-xs mt-1 block">{errors.departement}</span>}
                      </div>
                      <div className="mb-6">
                        <label htmlFor="objet" className="caption block mb-1.5">Objet de la demande *</label>
                        <textarea id="objet" name="objet" value={formData.objet} onChange={handleChange} rows={4} className={`input-field resize-none ${errors.objet ? 'border-red-400' : ''}`} placeholder="Décrivez votre projet ou votre demande..." />
                        {errors.objet && <span className="text-red-500 text-xs mt-1 block">{errors.objet}</span>}
                      </div>
                      <button type="submit" disabled={isSubmitting} className="btn-primary w-full flex items-center justify-center gap-2">
                        {isSubmitting ? (<><div className="spinner" />Envoi...</>) : (<>Envoyer ma demande <ArrowRight size={18} /></>)}
                      </button>
                      <p className="text-center text-[#94A3B8] text-xs mt-4">Réponse sous 24h • Devis gratuit et sans engagement</p>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
